/**
 * Top burgers composed from `get_public_burger_stats` (or the location-scoped
 * variant) + the matching `burgers` and `producers` rows.
 *
 * Used by:
 *   - LandingTopBurgers  (global, minCheckins=2, limit=5)
 *   - Discover           (country-scoped with global fallback, limit=5)
 *
 * Both call sites previously fetched stats / burgers / producers separately
 * and stitched them by hand. This hook does it once with proper caching.
 */
import { useQuery } from "@tanstack/react-query";
import { keys } from "@/data/keys";
import { supabase, unwrap } from "@/data/client";

export interface TopBurger {
  burger_id: string;
  slug: string;
  name: string;
  producer_id: string | null;
  producer_slug: string | null;
  producer_name: string | null;
  image_url: string | null;
  avg_rating: number;
  checkins_count: number;
}

export interface UseTopBurgersOptions {
  /** When provided, fetch country-scoped stats. Otherwise global. */
  locationId?: string | null;
  /** Minimum check-ins to include a burger (default 1). */
  minCheckins?: number;
  /** Max items to return (default 5). */
  limit?: number;
  /** When true and the location returns no rows, fall back to global. */
  fallbackToGlobal?: boolean;
  /** Disable the query entirely. */
  enabled?: boolean;
}

export function useTopBurgers(opts: UseTopBurgersOptions = {}) {
  const {
    locationId = null,
    minCheckins = 1,
    limit = 5,
    fallbackToGlobal = true,
    enabled = true,
  } = opts;

  return useQuery({
    queryKey: keys.burgers.list({ locationId, minCheckins, limit, fallbackToGlobal }),
    enabled,
    staleTime: 5_000,
    queryFn: async (): Promise<TopBurger[]> => {
      // 1. Fetch stats (location-scoped or global)
      let stats: { burger_id: string; avg_rating: number; checkins_count: number }[] = [];

      if (locationId) {
        const data = unwrap(
          await supabase.rpc("get_location_burger_stats", {
            location_id: locationId,
            min_checkins: minCheckins,
          }),
        );
        stats = (data ?? []).map((s) => ({
          burger_id: s.burger_id,
          avg_rating: Number(s.avg_rating ?? 0),
          checkins_count: Number(s.checkins_count ?? 0),
        }));
      }

      if (stats.length === 0 && (!locationId || fallbackToGlobal)) {
        const data = unwrap(await supabase.rpc("get_public_burger_stats"));
        stats = (data ?? []).map((s) => ({
          burger_id: s.burger_id,
          avg_rating: Number(s.avg_rating ?? 0),
          checkins_count: Number(s.checkins_count ?? 0),
        }));
      }

      // 2. Sort by rating, then by check-in count, and slice.
      const sorted = stats
        .filter((s) => s.checkins_count >= minCheckins)
        .sort((a, b) =>
          b.avg_rating !== a.avg_rating
            ? b.avg_rating - a.avg_rating
            : b.checkins_count - a.checkins_count,
        )
        .slice(0, limit);

      if (sorted.length === 0) return [];

      const ids = sorted.map((s) => s.burger_id);

      // 3. Hydrate burgers + producers in parallel.
      const [burgerRows, _] = await Promise.all([
        supabase
          .from("burgers")
          .select("id, slug, name, producer_id, canonical_image_url")
          .in("id", ids)
          .then(unwrap),
        Promise.resolve(null),
      ]);

      const producerIds = Array.from(
        new Set(burgerRows.map((b) => b.producer_id).filter(Boolean) as string[]),
      );

      const producerRows = producerIds.length
        ? unwrap(
            await supabase
              .from("producers")
              .select("id, slug, name")
              .in("id", producerIds),
          )
        : [];

      const burgerMap = new Map(burgerRows.map((b) => [b.id, b]));
      const producerMap = new Map(producerRows.map((p) => [p.id, p]));

      return sorted.map((s): TopBurger => {
        const burger = burgerMap.get(s.burger_id);
        const producer = burger?.producer_id ? producerMap.get(burger.producer_id) : undefined;
        return {
          burger_id: s.burger_id,
          slug: burger?.slug ?? s.burger_id,
          name: burger?.name ?? "Unknown",
          producer_id: burger?.producer_id ?? null,
          producer_slug: producer?.slug ?? null,
          producer_name: producer?.name ?? null,
          image_url: burger?.canonical_image_url ?? null,
          avg_rating: s.avg_rating,
          checkins_count: s.checkins_count,
        };
      });
    },
  });
}
